import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Save, RefreshCcw, GripVertical, Trash2, Plus, ChevronDown,
  ChevronRight, X, Loader2, Move, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

/* Decode the few HTML entities our cert text contains so the admin sees
   plain "Environment & Health" not "Environment &amp; Health". */
function decodeEntities(s = "") {
  return s
    .replace(/&amp;/g, "&")
    .replace(/&rsquo;/g, "'")
    .replace(/&lsquo;/g, "'")
    .replace(/&ldquo;/g, '"')
    .replace(/&rdquo;/g, '"')
    .replace(/&mdash;/g, "—")
    .replace(/&ndash;/g, "–");
}

function encodeAmp(s = "") {
  // Only re-escape standalone & (not already part of &amp;/&lt; etc.)
  return s.replace(/&(?!(?:amp|lt|gt|quot|apos|rsquo|lsquo|ldquo|rdquo|mdash|ndash|hellip|nbsp);)/g, "&amp;");
}

function newKey(existing) {
  const base = "x";
  let n = 1;
  while (existing.includes(`${base}${n}`)) n += 1;
  return `${base}${n}`;
}

function Field({ label, value, onChange, multiline = false, rows = 3, testid, placeholder, maxLength }) {
  const Comp = multiline ? Textarea : Input;
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <Comp
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        maxLength={maxLength}
        data-testid={testid}
        className="text-sm"
      />
    </label>
  );
}

function ColorDot({ col }) {
  return <span className="inline-block w-3.5 h-3.5 rounded-full border border-slate-300 shrink-0" style={{ background: col }}/>;
}

/**
 * Modal panel that lets the admin reorder cert categories, move cards
 * between sections (the "Why it matters" text travels with each card),
 * rename/recolor categories, edit card fields, add new cards/categories,
 * and delete them. Persists by hitting PUT /api/modules/:id/cert-data
 * which serialises the JS data back into the artifact_content.
 */
export default function CertStudio({ moduleId, onClose, onSaved }) {
  const [grid, setGrid] = useState(null);   // {categories, cards, order}
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({}); // catKey -> true
  const [selectedCardIdx, setSelectedCardIdx] = useState(null);

  // DnD state
  const [dragCardIdx, setDragCardIdx] = useState(null);
  const [dragSectionIdx, setDragSectionIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/modules/${moduleId}/cert-data`);
        if (cancelled) return;
        // Decode entities for UI editing — re-encoded on save
        const decoded = {
          categories: data.categories.map((c) => ({ ...c, name: decodeEntities(c.name) })),
          cards: data.cards.map((cd) => ({
            ...cd,
            name: decodeEntities(cd.name),
            desc: decodeEntities(cd.desc),
            why: decodeEntities(cd.why),
            std: decodeEntities(cd.std),
            cb: cd.cb ? decodeEntities(cd.cb) : "",
            alt: decodeEntities(cd.alt || ""),
          })),
          order: [...data.order],
        };
        setGrid(decoded);
        setOriginal(JSON.parse(JSON.stringify(decoded)));
        // expand only the categories that are currently in the order
        const ex = {};
        decoded.order.forEach((k) => { ex[k] = true; });
        setExpanded(ex);
      } catch (e) {
        toast.error(e?.response?.status === 404
          ? "This module doesn't have a Cert Studio grid"
          : "Failed to load certifications data");
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [moduleId, onClose]);

  const dirty = useMemo(() => {
    if (!grid || !original) return false;
    return JSON.stringify(grid) !== JSON.stringify(original);
  }, [grid, original]);

  /* ---------- Category mutations ---------- */
  const updateCategory = (key, patch) => {
    setGrid((g) => ({
      ...g,
      categories: g.categories.map((c) => (c.key === key ? { ...c, ...patch } : c)),
    }));
  };
  const addCategory = () => {
    setGrid((g) => {
      const key = newKey(g.categories.map((c) => c.key));
      const next = {
        ...g,
        categories: [...g.categories, { key, name: "New Section", col: "#1b2b4b", icon: g.categories[0]?.icon || "" }],
        order: [...g.order, key],
      };
      setExpanded((e) => ({ ...e, [key]: true }));
      return next;
    });
  };
  const removeCategory = (key) => {
    setGrid((g) => {
      const cardsHere = g.cards.filter((c) => c.c === key).length;
      if (cardsHere > 0) {
        toast.error(`Move or delete the ${cardsHere} card${cardsHere === 1 ? "" : "s"} in this section first.`);
        return g;
      }
      return {
        ...g,
        categories: g.categories.filter((c) => c.key !== key),
        order: g.order.filter((k) => k !== key),
      };
    });
  };

  /* ---------- Order (drag sections) ---------- */
  const moveSection = (fromKey, toKey) => {
    if (fromKey === toKey) return;
    setGrid((g) => {
      const ord = [...g.order];
      const fi = ord.indexOf(fromKey);
      const ti = ord.indexOf(toKey);
      if (fi < 0 || ti < 0) return g;
      ord.splice(fi, 1);
      ord.splice(ti, 0, fromKey);
      return { ...g, order: ord };
    });
  };

  /* ---------- Card mutations ---------- */
  const updateCard = (idx, patch) => {
    setGrid((g) => ({ ...g, cards: g.cards.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));
  };
  const moveCardToSection = (cardIdx, targetCatKey) => {
    setGrid((g) => {
      const card = g.cards[cardIdx];
      if (!card || card.c === targetCatKey) return g;
      return { ...g, cards: g.cards.map((c, i) => (i === cardIdx ? { ...c, c: targetCatKey } : c)) };
    });
    setExpanded((e) => ({ ...e, [targetCatKey]: true }));
  };
  const deleteCard = (idx) => {
    if (!window.confirm("Delete this card permanently? You can't undo without reloading.")) return;
    setGrid((g) => ({ ...g, cards: g.cards.filter((_, i) => i !== idx) }));
    setSelectedCardIdx(null);
  };
  const addCardToSection = (catKey) => {
    setGrid((g) => {
      const next = {
        ...g,
        cards: [
          ...g.cards,
          { c: catKey, name: "New Certification", img: "", alt: "", cb: "", std: "", desc: "", why: "" },
        ],
      };
      setSelectedCardIdx(next.cards.length - 1);
      return next;
    });
  };

  /* ---------- Save ---------- */
  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const payload = {
        categories: grid.categories.map((c) => ({ ...c, name: encodeAmp(c.name) })),
        cards: grid.cards.map((c) => ({
          ...c,
          name: encodeAmp(c.name),
          desc: encodeAmp(c.desc),
          why: encodeAmp(c.why),
          std: encodeAmp(c.std),
          cb: c.cb ? encodeAmp(c.cb) : null,
          alt: encodeAmp(c.alt || ""),
        })),
        order: grid.order,
      };
      await api.put(`/modules/${moduleId}/cert-data`, payload);
      toast.success("Certifications saved");
      setOriginal(JSON.parse(JSON.stringify(grid)));
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Render ---------- */
  if (loading) {
    return (
      <Shell onClose={onClose}>
        <div className="flex items-center justify-center h-64 text-slate-500 gap-2">
          <Loader2 className="animate-spin" size={16}/> Loading certifications…
        </div>
      </Shell>
    );
  }
  if (!grid) return null;

  const catByKey = Object.fromEntries(grid.categories.map((c) => [c.key, c]));
  const orderedCats = grid.order.map((k) => catByKey[k]).filter(Boolean);
  // Categories that exist but aren't in the order list — surface them at the bottom
  const orphanCats = grid.categories.filter((c) => !grid.order.includes(c.key));

  return (
    <Shell
      onClose={onClose}
      dirty={dirty}
      saving={saving}
      onSave={handleSave}
      onReset={() => {
        if (!dirty) return;
        if (window.confirm("Discard all unsaved changes?")) {
          setGrid(JSON.parse(JSON.stringify(original)));
        }
      }}
    >
      <div className="grid grid-cols-12 gap-4 h-full overflow-hidden">
        {/* ---- LEFT: section + card tree ---- */}
        <div className="col-span-7 overflow-y-auto pr-2" data-testid="cert-studio-tree">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Sections · {grid.order.length}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={addCategory}
              data-testid="cert-studio-add-section"
              className="h-7 text-xs"
            >
              <Plus size={12} className="mr-1"/> Add section
            </Button>
          </div>

          {orderedCats.map((cat) => {
            const cards = grid.cards
              .map((c, i) => ({ ...c, _idx: i }))
              .filter((c) => c.c === cat.key);
            return (
              <div
                key={cat.key}
                draggable
                onDragStart={() => setDragSectionIdx(cat.key)}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={() => { if (dragSectionIdx) moveSection(dragSectionIdx, cat.key); setDragSectionIdx(null); }}
                onDragEnd={() => setDragSectionIdx(null)}
                className="mb-3 rounded-lg border border-slate-200 bg-white"
                data-testid={`cert-studio-section-${cat.key}`}
              >
                <div className="flex items-center gap-2 p-2.5 border-b border-slate-100">
                  <GripVertical size={14} className="text-slate-400 cursor-grab"/>
                  <ColorDot col={cat.col}/>
                  <Input
                    value={cat.name}
                    onChange={(e) => updateCategory(cat.key, { name: e.target.value })}
                    data-testid={`cert-studio-section-name-${cat.key}`}
                    className="h-7 text-sm font-medium flex-1"
                  />
                  <Input
                    value={cat.col}
                    onChange={(e) => updateCategory(cat.key, { col: e.target.value })}
                    data-testid={`cert-studio-section-color-${cat.key}`}
                    className="h-7 w-24 text-xs font-mono"
                    title="Hex color"
                  />
                  <button
                    onClick={() => setExpanded((e) => ({ ...e, [cat.key]: !e[cat.key] }))}
                    className="text-slate-400 hover:text-slate-700 p-1"
                    data-testid={`cert-studio-section-toggle-${cat.key}`}
                    title={expanded[cat.key] ? "Collapse" : "Expand"}
                  >
                    {expanded[cat.key] ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                  </button>
                  <button
                    onClick={() => removeCategory(cat.key)}
                    className="text-slate-400 hover:text-rose-600 p-1"
                    data-testid={`cert-studio-section-delete-${cat.key}`}
                    title={`Delete section (must be empty)`}
                  >
                    <Trash2 size={13}/>
                  </button>
                </div>

                {expanded[cat.key] && (
                  <div className="p-2 space-y-1.5 min-h-[40px]"
                       onDragOver={(e) => { if (dragCardIdx != null) e.preventDefault(); }}
                       onDrop={() => { if (dragCardIdx != null) { moveCardToSection(dragCardIdx, cat.key); setDragCardIdx(null); } }}>
                    {cards.length === 0 && (
                      <div className="text-[11px] text-slate-400 italic px-2 py-1">No cards — drag one here or add new</div>
                    )}
                    {cards.map((card) => (
                      <div
                        key={card._idx}
                        draggable
                        onDragStart={(e) => { setDragCardIdx(card._idx); e.stopPropagation(); }}
                        onDragEnd={() => setDragCardIdx(null)}
                        onClick={() => setSelectedCardIdx(card._idx)}
                        className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                          selectedCardIdx === card._idx
                            ? "border-[#E53935] bg-[#fff5f5]"
                            : "border-slate-200 bg-slate-50 hover:border-slate-300"
                        }`}
                        data-testid={`cert-studio-card-${card._idx}`}
                      >
                        <Move size={12} className="text-slate-400 cursor-grab"/>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-slate-800 truncate">{card.name || "(unnamed)"}</div>
                          <div className="text-[10px] text-slate-500 truncate">{card.std || card.cb || "—"}</div>
                        </div>
                        {/* Quick "move to" dropdown */}
                        <select
                          value={card.c}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => moveCardToSection(card._idx, e.target.value)}
                          data-testid={`cert-studio-card-move-${card._idx}`}
                          className="h-6 text-[11px] border border-slate-200 rounded bg-white"
                        >
                          {grid.categories.map((c) => (
                            <option key={c.key} value={c.key}>{c.name.slice(0, 28)}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addCardToSection(cat.key)}
                      className="w-full h-7 text-xs"
                      data-testid={`cert-studio-add-card-${cat.key}`}
                    >
                      <Plus size={12} className="mr-1"/> Add card to {cat.name.slice(0, 24)}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}

          {orphanCats.length > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-2 inline-flex items-center gap-1.5">
                <AlertCircle size={12}/> Hidden categories ({orphanCats.length})
              </div>
              <p className="text-[11px] text-amber-800 mb-2">
                These category keys exist in the data but aren&apos;t rendered. Useful for storing
                old categories without losing their cards. Click to re-add to the render order.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {orphanCats.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => setGrid((g) => ({ ...g, order: [...g.order, c.key] }))}
                    data-testid={`cert-studio-orphan-${c.key}`}
                    className="text-[11px] bg-white border border-amber-300 px-2 py-1 rounded hover:bg-amber-100"
                  >
                    + {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ---- RIGHT: selected card editor ---- */}
        <div className="col-span-5 overflow-y-auto pl-2 border-l border-slate-200" data-testid="cert-studio-detail">
          {selectedCardIdx == null || !grid.cards[selectedCardIdx] ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 p-6">
              <Sparkles size={28} className="mb-3 text-slate-300"/>
              <p className="text-sm">Pick a card on the left to edit its details.</p>
              <p className="text-[11px] mt-2 max-w-xs">Drag a card from one section to another, or use the dropdown next to each card. The &ldquo;Why it matters&rdquo; text always travels with the card.</p>
            </div>
          ) : (
            <CardDetail
              card={grid.cards[selectedCardIdx]}
              categories={grid.categories}
              onChange={(patch) => updateCard(selectedCardIdx, patch)}
              onDelete={() => deleteCard(selectedCardIdx)}
            />
          )}
        </div>
      </div>
    </Shell>
  );
}

function CardDetail({ card, categories, onChange, onDelete }) {
  return (
    <div className="space-y-3 pb-6">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Card details</div>
        <button
          onClick={onDelete}
          className="text-rose-600 hover:text-rose-700 text-[11px] inline-flex items-center gap-1"
          data-testid="cert-studio-detail-delete"
        >
          <Trash2 size={12}/> Delete
        </button>
      </div>

      <label className="block">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Section</div>
        <select
          value={card.c}
          onChange={(e) => onChange({ c: e.target.value })}
          data-testid="cert-studio-detail-section"
          className="w-full h-8 border border-slate-200 rounded text-sm px-2 bg-white"
        >
          {categories.map((c) => (
            <option key={c.key} value={c.key}>{c.name}</option>
          ))}
        </select>
      </label>

      <Field label="Card title" value={card.name} onChange={(v) => onChange({ name: v })}
             testid="cert-studio-detail-name" maxLength={400}/>
      <Field label='Front-of-card label (e.g. &ldquo;Certified by ARAI&rdquo;)' value={card.cb}
             onChange={(v) => onChange({ cb: v })}
             testid="cert-studio-detail-cb" placeholder="optional" maxLength={400}/>
      <Field label="Standard / Test method" value={card.std} onChange={(v) => onChange({ std: v })}
             testid="cert-studio-detail-std" maxLength={600}/>
      <Field label="What it certifies (description)" value={card.desc}
             onChange={(v) => onChange({ desc: v })}
             multiline rows={4} testid="cert-studio-detail-desc" maxLength={4000}/>
      <Field label="Why it matters — the sales angle" value={card.why}
             onChange={(v) => onChange({ why: v })}
             multiline rows={4} testid="cert-studio-detail-why" maxLength={4000}/>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Logo key" value={card.img} onChange={(v) => onChange({ img: v })}
               testid="cert-studio-detail-img" placeholder="e.g. igbc" maxLength={80}/>
        <Field label="Logo alt-text" value={card.alt} onChange={(v) => onChange({ alt: v })}
               testid="cert-studio-detail-alt" maxLength={400}/>
      </div>
      <div className="text-[10px] text-slate-500 leading-snug bg-slate-50 border border-slate-200 rounded p-2">
        <strong>Logo key</strong> references an entry in the module&apos;s <code>LOGO</code> object (base64 data URI map).
        Use an existing key like <code>igbc</code>, <code>arai</code>, etc. To add a brand-new logo image,
        upload it via the Visual Editor&apos;s image controls — the studio works with what&apos;s already defined.
      </div>
    </div>
  );
}

function Shell({ children, onClose, dirty, saving, onSave, onReset }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="cert-studio-modal">
      <div className="w-full max-w-6xl h-[88vh] bg-white rounded-xl shadow-2xl flex flex-col">
        <header className="flex items-center justify-between gap-4 px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles size={16} className="text-[#E53935]"/>
            <div className="min-w-0">
              <div className="font-display text-lg text-[#222222] leading-tight">Certifications Studio</div>
              <div className="text-[11px] text-slate-500">Move cards between sections, rename headers, edit any field. The sales angle travels with each card.</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {onReset && (
              <Button
                size="sm" variant="outline"
                onClick={onReset}
                disabled={!dirty || saving}
                className="h-8 text-xs"
                data-testid="cert-studio-reset"
              >
                <RefreshCcw size={12} className="mr-1"/> Discard
              </Button>
            )}
            {onSave && (
              <Button
                size="sm"
                onClick={onSave}
                disabled={!dirty || saving}
                className="h-8 text-xs bg-[#E53935] hover:bg-[#c62828] text-white"
                data-testid="cert-studio-save"
              >
                {saving ? <Loader2 className="animate-spin mr-1" size={12}/> : <Save size={12} className="mr-1"/>}
                {dirty ? "Save changes" : "Saved"}
              </Button>
            )}
            <button
              onClick={() => {
                if (dirty && !window.confirm("You have unsaved changes. Close anyway?")) return;
                onClose?.();
              }}
              className="p-1.5 text-slate-400 hover:text-slate-700"
              data-testid="cert-studio-close"
            >
              <X size={16}/>
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-hidden p-4">{children}</div>
      </div>
    </div>
  );
}
